/**
 * UID-155 (B3) receiver-side type widening: the B0 `PartAttachmentPresignTicket`
 * contract (`schema.gen.ts`) is deliberately KEYLESS — no `objectKey` — because
 * the S3 object key is server-only (never exposed to the browser). The
 * hand-authored `PresignUploadTicket` (`./types.ts`) predates that contract and
 * REQUIRES `objectKey`, so a keyless ticket is not structurally assignable to
 * it, and this module's exports couldn't accept both ticket shapes.
 *
 * The fix is narrowing on the RECEIVER side, not the contract: `putFileToS3`/
 * `assertWithinMaxBytes` only ever read `method`/`url`/`headers`/`maxBytes` off
 * the ticket — neither function has ever touched `objectKey` or `uploadId`.
 * `S3PutTicket` names exactly that subset, so both `PresignUploadTicket` (UID-40,
 * has `objectKey`) and `PartAttachmentPresignTicket` (UID-152, keyless) satisfy
 * it structurally — no contract change, no cast, no `objectKey` ever rendered.
 */
export interface S3PutTicket {
  readonly url: string
  readonly method: 'PUT'
  readonly headers: Readonly<Record<string, string>>
  readonly maxBytes: number
}

/**
 * UID-124 — the real browser→S3 byte transfer that UID-41 intentionally
 * deferred (see `UploadsPage.tsx`'s former doc-comment on `uploadOne`).
 * `presignUpload` (UID-40/UID-105) only mints the ticket; this module does
 * the actual `PUT` of the file's bytes to `ticket.url`.
 *
 * Uses `XMLHttpRequest` rather than `fetch` because `fetch` has no upload
 * progress event — `xhr.upload.onprogress` is the only way to report real
 * byte-level progress to the UI.
 *
 * `ticket.headers` (UID-105's SIGNED headers) MUST be forwarded to the PUT
 * or S3 returns `403 SignatureDoesNotMatch` — with one exception:
 * `Content-Length` is a browser-forbidden header (the XHR/fetch spec
 * disallows setting it from script); real browsers silently ignore
 * `setRequestHeader('Content-Length', …)` and compute it themselves from
 * the request body. That's fine here because the browser-computed length
 * always equals `file.size`, which is exactly the `sizeBytes` that was
 * signed when the ticket was minted — so the signature still holds. We
 * skip replaying it (case-insensitively) to avoid a false-confidence
 * no-op call (and a console warning in some browsers), and forward every
 * other signed header (the SSE-KMS ones) verbatim.
 */
export interface PutToS3Options {
  readonly onProgress?: (percent: number) => void
}

/** Header names the browser forbids scripts from setting via
 * `XMLHttpRequest.setRequestHeader` — it manages them itself. Filtered out
 * of `ticket.headers` before replay (see module doc-comment). */
const FORBIDDEN_REQUEST_HEADERS = new Set(['content-length'])

export class UploadTooLargeError extends Error {
  constructor(maxBytes: number) {
    super(`File exceeds the allowed upload size (max ${maxBytes.toLocaleString()} bytes).`)
    this.name = 'UploadTooLargeError'
  }
}

/** Fail fast client-side before opening the connection — the size limit is
 * enforced server-side too (S3 will reject an over-signed Content-Length),
 * this is purely a faster, clearer error for the user. */
export function assertWithinMaxBytes(file: File, ticket: S3PutTicket): void {
  if (file.size > ticket.maxBytes) {
    throw new UploadTooLargeError(ticket.maxBytes)
  }
}

/** Floor timeout for a CAD-file PUT — even a tiny file gets at least this
 * long, so we never trip on connection setup / TLS handshake jitter alone. */
const UPLOAD_TIMEOUT_FLOOR_MS = 2 * 60 * 1000 // 2 minutes

/** Assumed worst-case-but-real sustained throughput for a legit slow link
 * (e.g. a spotty office wifi or hotel connection) — used to scale the
 * timeout for large CAD files so we don't trip a big-but-legit upload on a
 * slow connection, while still bounding how long a stalled PUT can hang. */
const ASSUMED_MIN_THROUGHPUT_BYTES_PER_SEC = 256 * 1024 // 256 KB/s

/** PUTs `file`'s bytes to `ticket.url`, forwarding `ticket.headers` (minus
 * the browser-forbidden `Content-Length`, see module doc-comment).
 * Resolves on any 2xx; rejects with a real error message otherwise
 * (non-2xx, network error, or timeout). */
export function putFileToS3(
  file: File,
  ticket: S3PutTicket,
  { onProgress }: PutToS3Options = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(ticket.method, ticket.url, true)

    for (const [name, value] of Object.entries(ticket.headers)) {
      if (FORBIDDEN_REQUEST_HEADERS.has(name.toLowerCase())) continue
      xhr.setRequestHeader(name, value)
    }

    // Scale the timeout off the file size (with a floor) so `ontimeout`
    // can actually fire on a stalled connection without tripping on a
    // large-but-legitimate upload over a slow link.
    xhr.timeout = Math.max(
      UPLOAD_TIMEOUT_FLOOR_MS,
      (file.size / ASSUMED_MIN_THROUGHPUT_BYTES_PER_SEC) * 1000,
    )

    xhr.upload.onprogress = (event) => {
      if (!onProgress) return
      const percent = event.lengthComputable ? Math.round((event.loaded / event.total) * 100) : 0
      onProgress(percent)
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100)
        resolve()
        return
      }
      reject(new Error(`Upload to storage failed (status ${xhr.status}).`))
    }

    xhr.onerror = () => reject(new Error('Upload to storage failed due to a network error.'))
    xhr.ontimeout = () => reject(new Error('Upload to storage timed out.'))
    xhr.onabort = () => reject(new Error('Upload to storage was aborted.'))

    xhr.send(file)
  })
}
